import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Form, Button, Container, Alert, Row, Col } from 'react-bootstrap'
import { warehouseAPI } from '../api/warehouseAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'

function WarehouseScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const [warehouse, setWarehouse] = useState({
        _id: '',
        name: '',
        address: '',
        is_active: true
    })
    const [error, setError] = useState(null)

    useEffect(() => {
        async function fetchWarehouse() {
            const data = await warehouseAPI.getById(id);
            setWarehouse(data)
        }
        fetchWarehouse()
    }, [id])

    const submitHandler = async (e) => {
        e.preventDefault()
        try {
            await warehouseAPI.update(id, warehouse);
            navigate('/warehouses')
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleToggleActive = async (e) => {
        e.preventDefault();
        
        // Show confirmation dialog only when deactivating
        if (warehouse.is_active) {
            if (!window.confirm('Are you sure you want to deactivate this warehouse?')) {
                return;
            }
        }

        try {
            const updatedWarehouse = {
                ...warehouse,
                is_active: !warehouse.is_active
            };
            
            await warehouseAPI.update(id, updatedWarehouse);
            
            setWarehouse(updatedWarehouse);
        } catch (error) {
            console.error('Error toggling warehouse status:', error);
            alert(`Failed to update warehouse: ${error.response?.data?.message || error.message}`);
        }
    };

    if (permissionsLoading) {
        return (
            <Container className="py-3">
                <div className="text-center">Loading...</div>
            </Container>
        )
    }

    if (!hasPermission('locations.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to update warehouses.
                </Alert>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <h1>Update Warehouse</h1>
                    <Form onSubmit={submitHandler}>
                        <Form.Group className="mb-4" controlId="name">
                            <Form.Label>Name*</Form.Label>
                            <Form.Control
                                type="text"
                                value={warehouse.name}
                                onChange={(e) => setWarehouse({...warehouse, name: e.target.value})}
                                disabled={!warehouse.is_active}
                                placeholder="Enter name"
                                style={{ opacity: warehouse.is_active ? 1 : 0.6 }}
                                required
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4" controlId="location">
                            <Form.Label>Address</Form.Label>
                            <Form.Control
                                type="text"
                                value={warehouse.address}
                                onChange={(e) => setWarehouse({...warehouse, address: e.target.value})}
                                disabled={!warehouse.is_active}
                                placeholder="Enter address"
                                style={{ opacity: warehouse.is_active ? 1 : 0.6 }}
                                autoComplete="off"
                                spellCheck="false"
                            />
                        </Form.Group>
                        {error && (
                            <Alert variant="danger" onClose={() => setError(null)} dismissible>
                                {error}
                            </Alert>
                        )}
                        <Row className="mt-3">
                            <Col>
                                {warehouse.is_active ? (
                                    <div className="d-flex gap-2">
                                        <Button 
                                            variant="primary" 
                                            type="submit"
                                        >
                                            Update
                                        </Button>
                                        <Button 
                                            variant="info"
                                            onClick={() => navigate(`/warehouse/${warehouse._id}/locations`, {
                                                state: { warehouseName: warehouse.name }
                                            })}
                                        >
                                            Locations
                                        </Button>
                                        <Button 
                                            variant="danger"
                                            onClick={handleToggleActive}
                                        >
                                            Deactivate
                                        </Button>
                                        <Button 
                                            variant="outline-primary" 
                                            onClick={() => navigate('/warehouses')}
                                        >
                                            <i className="fas fa-arrow-left me-2"></i>
                                            Back to List
                                        </Button>
                                    </div>
                                ) : (
                                    <Button 
                                        variant="success"
                                        onClick={handleToggleActive}
                                    >
                                        Activate
                                    </Button>
                                )}
                            </Col>
                        </Row>
                        {!warehouse.is_active && (
                            <Alert variant="warning" className="mt-3">
                                This warehouse is currently inactive. Activate it to make changes.
                            </Alert>
                        )}
                    </Form>
                </Col>
            </Row>
        </Container>
    )
}

export default WarehouseScreen 